// src/components/metrics/SampleOrderCostPerUser.js

import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  Users,
  TrendingUp,
  DollarSign,
  Award,
  Target,
} from "lucide-react";

const isConvertedStatus = (status) => {
  if (!status) return false;
  const normalized = String(status).toLowerCase();
  // Handles "Converted to Order", "converted", etc.
  return normalized.includes("converted");
};

const SampleOrderCostPerUser = ({
  companyId,
  maxRows = 6,
  currentUserId, // ID of logged-in user (optional)
  isAdmin = false, // if true => show everyone (except customers)
  sampleOrders = null,
  companyUsers = null,
}) => {
  const db = getFirestore();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!companyId) return;

    let unsubscribesampleOrders;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        // 1) Load inventory (productId -> price)
        const inventoryRef = collection(
          db,
          "companies",
          companyId,
          "inventory"
        );
        const inventorySnap = await getDocs(inventoryRef);

        const priceMap = {};
        inventorySnap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const price =
            typeof data.price === "number"
              ? data.price
              : Number(data.price) || 0;
          priceMap[docSnap.id] = price;
        });

        const userMap = {};
        if (Array.isArray(companyUsers)) {
          companyUsers.forEach((user) => {
            const id = user?.id || user?.uid;
            if (!id) return;
            userMap[id] = {
              role: user?.role || "endUser",
              fullName: user?.fullName || user?.displayName || user?.email || id,
            };
          });
        } else {
          // 2) Load users for this company so we can filter out customers
          const usersRef = collection(db, "users");
          const qUsers = query(
            usersRef,
            where("companyId", "==", companyId)
          );
          const usersSnap = await getDocs(qUsers);

          usersSnap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            userMap[docSnap.id] = {
              role: data.role || "endUser",
              fullName: data.fullName || data.email || docSnap.id,
            };
          });
        }

        const shouldFilterToCurrentUser = !isAdmin && !!currentUserId;
        const buildRows = (records) => {
            const userStats = {};

            (Array.isArray(records) ? records : []).forEach((data) => {
              const record = data || {};

              // Ignore hidden requests
              if (record.hidden === true) return;

              const userId = record.createdBy;
              if (!userId) return;

              const userInfo = userMap[userId] || {};
              const role = userInfo.role || "endUser";

              // ❌ Skip customer users completely from this metric
              if (role === "customer") {
                return;
              }

              // ✅ Only filter to the current user if not admin
              if (shouldFilterToCurrentUser && userId !== currentUserId) {
                return;
              }

              const name =
                userInfo.fullName ||
                record.createdByName ||
                userId;

              if (!userStats[userId]) {
                userStats[userId] = {
                  userId,
                  name,
                  total: 0,
                  converted: 0,
                  cost: 0,
                };
              }

              const status = record.status || "";
              userStats[userId].total += 1;

              if (isConvertedStatus(status)) {
                userStats[userId].converted += 1;
              }

              const productId = record.productId;
              const quantity = Number(record.quantity) || 0;
              const price = priceMap[productId] ?? 0;
              const cost = price * quantity;

              const normalizedStatus = String(status).toLowerCase();
              const isCancelledOrRejected =
                normalizedStatus.includes("cancel") ||
                normalizedStatus.includes("reject");

              if (!isCancelledOrRejected) {
                userStats[userId].cost += cost;
              }
            });

            const list = Object.values(userStats)
              .map((u) => ({
                ...u,
                rate: u.total > 0 ? u.converted / u.total : 0,
              }))
              // Sort by cost desc, then conversion rate desc, then total desc
              .sort((a, b) => {
                if (b.cost !== a.cost) return b.cost - a.cost;
                if (b.rate !== a.rate) return b.rate - a.rate;
                return b.total - a.total;
              })
              .slice(0, maxRows);

            setRows(list);
            setLoading(false);
        };

        if (Array.isArray(sampleOrders)) {
          buildRows(sampleOrders);
          return;
        }

        // 3) Subscribe to all sampleOrders for this company
        const sampleRef = collection(
          db,
          "companies",
          companyId,
          "sampleOrders"
        );

        unsubscribesampleOrders = onSnapshot(
          sampleRef,
          (snapshot) => {
            buildRows(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
          },
          (err) => {
            console.error(
              "Error loading per-user metrics (snapshot):",
              err
            );
            setError(
              err.message || "Failed to load conversion metrics."
            );
            setLoading(false);
          }
        );
      } catch (err) {
        console.error(
          "Error loading per-user metrics (outer):",
          err
        );
        setError(
          err.message || "Failed to load conversion metrics."
        );
        setLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubscribesampleOrders) {
        unsubscribesampleOrders();
      }
    };
  }, [companyId, db, maxRows, currentUserId, isAdmin, sampleOrders, companyUsers]);

  const formatPercent = (value) =>
    `${Math.round((Number(value) || 0) * 100)}%`;

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    try {
      return num.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    } catch {
      return `$${num.toFixed(0)}`;
    }
  };

  const getConversionColor = (rate) => {
    if (rate >= 0.7) return "from-green-500 to-emerald-600";
    if (rate >= 0.4) return "from-blue-500 to-indigo-600";
    return "from-gray-400 to-gray-500";
  };

  const getConversionBadgeColor = (rate) => {
    if (rate >= 0.7) return "bg-green-50 text-green-700 border-green-200";
    if (rate >= 0.4) return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white rounded-lg shadow-sm">
              <Users className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {isAdmin ? "Team Performance" : "My Performance"}
              </h3>
              <p className="text-[10px] text-gray-500">
                Conversion rates (endUsers & reps only)
              </p>
            </div>
          </div>
          {loading && (
            <div className="flex items-center gap-1.5 text-gray-400 text-[10px]">
              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
              <span>Loading…</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-red-600">
                !
              </span>
            </div>
            <span>{error}</span>
          </div>
        )}

        {!error && rows.length === 0 && !loading && (
          <div className="text-center py-6">
            <Target className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">
              No sample requests found yet
            </p>
          </div>
        )}

        {!error && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map((user, index) => (
              <div
                key={user.userId}
                className="group relative p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                style={{
                  animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  {/* Left: User info */}
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div
                      className={`p-1.5 rounded-lg bg-gradient-to-br ${getConversionColor(
                        user.rate
                      )} flex-shrink-0`}
                    >
                      <Award className="w-3 h-3 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {user.name}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-gray-500">
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {user.converted}/{user.total}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatCurrency(user.cost)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Conversion rate badge */}
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-semibold border ${getConversionBadgeColor(
                      user.rate
                    )} flex-shrink-0`}
                  >
                    {formatPercent(user.rate)}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${getConversionColor(
                      user.rate
                    )} transition-all duration-500`}
                    style={{ width: `${user.rate * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SampleOrderCostPerUser;
